import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import Pagination from './Pagination';
import { Stethoscope, Plus, Edit, Trash2, X, Eye, Save, Search, Building2, UserPlus, Clock, Check, XCircle } from 'lucide-react';

const DentistManagement = ({ currentBranch, user }) => {
  const { print_lang } = useLanguage();
  const [dentistList, setDentistList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'add'
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Search dentist to add
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchDentist, setSearchDentist] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addFormData, setAddFormData] = useState({
    dentist_id: null,
    df_percentage: '',
    df_type: ''
  });

  // Pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Pending requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showPendingSection, setShowPendingSection] = useState(true);

  // Dentist count and limit
  const [dentistCount, setDentistCount] = useState({ current: 0, limit: 10, available: 10 });

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchDentists();
      fetchPendingRequests();
      fetchDentistCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch]);

  const fetchDentists = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`/api/dentist-work?branch_id=${currentBranch.branch_id}`);
      setDentistList(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch dentists:', error);
      setDentistList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await fetchWithAuth(`/api/dentist-work/pending?branch_id=${currentBranch.branch_id}`);
      setPendingRequests(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
      setPendingRequests([]);
    }
  };

  const fetchDentistCount = async () => {
    try {
      const response = await fetchWithAuth(`/api/dentist-work/count?branch_id=${currentBranch.branch_id}`);
      if (response?.data) {
        setDentistCount(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch dentist count:', error);
    }
  };

  const handleApproveRequest = async (workId, dentistName) => {
    // Check limit before trying to approve
    if (dentistCount.available <= 0) {
      alert(print_lang('dentist_limit_reached', 'Dentist limit reached. Please contact admin.'));
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/dentist-work/${workId}/approve`, {
        method: 'PUT',
        body: JSON.stringify({})
      });
      if (response.success) {
        fetchPendingRequests();
        fetchDentists();
        fetchDentistCount();
      }
    } catch (error) {
      console.error('Failed to approve request:', error);
      // Check if it's a limit error
      if (error.message?.includes('limit') || error.message?.includes('Dentist limit')) {
        alert(print_lang('dentist_limit_reached', 'Dentist limit reached. Please contact admin.'));
        fetchDentistCount();
      } else {
        alert(error.message || print_lang('failed_to_approve', 'Failed to approve request'));
      }
    }
  };

  const handleRejectRequest = async (workId, dentistName) => {
    if (!window.confirm(print_lang('confirm_reject_request', `Are you sure you want to reject ${dentistName}'s request?`))) {
      return;
    }
    try {
      const response = await fetchWithAuth(`/api/dentist-work/${workId}/reject`, {
        method: 'PUT',
        body: JSON.stringify({})
      });
      if (response.success) {
        fetchPendingRequests();
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      alert(error.message || print_lang('failed_to_reject', 'Failed to reject request'));
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/${path}`;
  };

  const handleView = (dentist) => {
    setSelectedDentist(dentist);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (dentist) => {
    setSelectedDentist(dentist);
    setFormData({
      df_percentage: dentist.df_percentage || '',
      df_type: dentist.df_type || 'percentage',
      status: dentist.work_status || 'active'
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSwitchToEdit = () => {
    if (selectedDentist) {
      setFormData({
        df_percentage: selectedDentist.df_percentage || '',
        df_type: selectedDentist.df_type || 'percentage',
        status: selectedDentist.work_status || 'active'
      });
      setModalMode('edit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await fetchWithAuth(`/api/dentist-work/${selectedDentist.work_id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      setShowModal(false);
      fetchDentists();
    } catch (error) {
      console.error('Failed to save dentist assignment:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (workId, dentistName) => {
    if (!window.confirm(
      print_lang('confirm_remove_dentist', `Are you sure you want to remove ${dentistName} from this branch?`)
    )) return;

    try {
      await fetchWithAuth(`/api/dentist-work/${workId}`, { method: 'DELETE' });
      fetchDentists();
      fetchDentistCount();
    } catch (error) {
      console.error('Failed to remove dentist:', error);
      alert(error.message);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDentist(null);
    setFormData({});
  };

  // Add dentist to branch
  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setSearchDentist('');
    setSearchResults([]);
    setAddFormData({
      dentist_id: null,
      df_percentage: '',
      df_type: ''
    });
  };

  const handleSearchDentist = async () => {
    if (!searchDentist.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetchWithAuth(`/api/dentists?search=${encodeURIComponent(searchDentist)}`);
      // Filter out dentists already in this branch
      const existingIds = new Set(dentistList.map(d => d.dentist_id));
      const filtered = (response?.data || []).filter(d => !existingIds.has(d.dentist_id));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Failed to search dentists:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectDentist = (dentist) => {
    setAddFormData({
      ...addFormData,
      dentist_id: dentist.dentist_id,
      selectedDentist: dentist
    });
  };

  const handleAddToBranch = async () => {
    if (!addFormData.dentist_id) {
      alert(print_lang('select_dentist_first', 'Please select a dentist first'));
      return;
    }

    // Check limit before trying to add
    if (dentistCount.available <= 0) {
      alert(print_lang('dentist_limit_reached', 'Dentist limit reached. Please contact admin.'));
      return;
    }

    setIsSaving(true);
    try {
      await fetchWithAuth('/api/dentist-work', {
        method: 'POST',
        body: JSON.stringify({
          dentist_id: addFormData.dentist_id,
          branch_id: currentBranch.branch_id,
          df_percentage: addFormData.df_percentage || null,
          df_type: addFormData.df_type || null
        })
      });

      setShowAddModal(false);
      fetchDentists();
      fetchDentistCount();
    } catch (error) {
      console.error('Failed to add dentist:', error);
      // Check if it's a limit error
      if (error.message?.includes('limit') || error.message?.includes('Dentist limit')) {
        alert(print_lang('dentist_limit_reached', 'Dentist limit reached. Please contact admin.'));
        fetchDentistCount();
      } else {
        alert(error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const specialtyLabels = {
    general: print_lang('general_dentistry', 'General Dentistry'),
    orthodontics: print_lang('orthodontics', 'Orthodontics'),
    endodontics: print_lang('endodontics', 'Endodontics'),
    periodontics: print_lang('periodontics', 'Periodontics'),
    prosthodontics: print_lang('prosthodontics', 'Prosthodontics'),
    oral_surgery: print_lang('oral_surgery', 'Oral Surgery'),
    pediatric: print_lang('pediatric_dentistry', 'Pediatric Dentistry'),
    cosmetic: print_lang('cosmetic_dentistry', 'Cosmetic Dentistry'),
    implantology: print_lang('implantology', 'Implantology')
  };

  // Filter dentists based on search
  const filteredDentists = dentistList.filter(dentist => {
    const matchesSearch = !searchTerm ||
      dentist.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dentist.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dentist.license_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Pagination
  const totalCount = filteredDentists.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedDentists = filteredDentists.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Stethoscope size={28} />
          {print_lang('dentist_management', 'Dentist Management')}
        </h1>

        <button
          onClick={handleOpenAddModal}
          disabled={dentistCount.available <= 0}
          className={`btn flex items-center gap-2 ${dentistCount.available <= 0 ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'}`}
          title={dentistCount.available <= 0 ? print_lang('dentist_limit_reached', 'Dentist limit reached') : ''}
        >
          <UserPlus size={18} />
          {print_lang('add_dentist_to_branch', 'Add Dentist to Branch')}
        </button>
      </div>

      {/* Branch Info */}
      <div className="card mb-6 bg-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={24} className="text-blue-600" />
            <div>
              <p className="text-sm text-blue-600">{print_lang('current_branch', 'Current Branch')}</p>
              <p className="font-semibold text-blue-800">{currentBranch?.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-600">{print_lang('dentist_limit', 'Dentist Limit')}</p>
            <p className={`font-semibold ${dentistCount.available <= 0 ? 'text-red-600' : dentistCount.available <= 2 ? 'text-yellow-600' : 'text-blue-800'}`}>
              {dentistCount.current} / {dentistCount.limit}
              {dentistCount.available <= 0 && (
                <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                  {print_lang('full', 'Full')}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Pending Requests Section */}
      {pendingRequests.length > 0 && (
        <div className="card mb-6 bg-yellow-50 border-l-4 border-yellow-500">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowPendingSection(!showPendingSection)}
          >
            <div className="flex items-center gap-3">
              <Clock size={24} className="text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">
                  {print_lang('pending_requests', 'Pending Requests')} ({pendingRequests.length})
                </p>
                <p className="text-sm text-yellow-600">
                  {print_lang('dentists_want_to_join', 'Dentists want to join this branch')}
                </p>
              </div>
            </div>
            <button className="text-yellow-600 hover:text-yellow-800">
              {showPendingSection ? '▲' : '▼'}
            </button>
          </div>

          {showPendingSection && (
            <div className="mt-4 space-y-3">
              {pendingRequests.map(request => (
                <div key={request.work_id} className="bg-white rounded-lg p-4 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-4">
                    {request.images_path ? (
                      <img
                        src={getImageUrl(request.images_path)}
                        alt={request.dentist_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <Stethoscope size={20} className="text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{request.dentist_name}</p>
                      <p className="text-sm text-gray-500">{request.dentist_email}</p>
                      <div className="flex gap-3 text-sm text-gray-400 mt-1">
                        {request.specialty && (
                          <span>{specialtyLabels[request.specialty] || request.specialty}</span>
                        )}
                        {request.license_number && (
                          <span className="font-mono">{request.license_number}</span>
                        )}
                      </div>
                      {request.requested_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          {print_lang('requested', 'Requested')}: {new Date(request.requested_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproveRequest(request.work_id, request.dentist_name)}
                      className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      title={print_lang('approve', 'Approve')}
                    >
                      <Check size={16} />
                      {print_lang('approve', 'Approve')}
                    </button>
                    <button
                      onClick={() => handleRejectRequest(request.work_id, request.dentist_name)}
                      className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      title={print_lang('reject', 'Reject')}
                    >
                      <XCircle size={16} />
                      {print_lang('reject', 'Reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="card mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={print_lang('search_dentist', 'Search by name, email, or license...')}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{print_lang('name', 'Name')}</th>
                <th>{print_lang('specialty', 'Specialty')}</th>
                <th>{print_lang('license_number', 'License No.')}</th>
                <th>{print_lang('doctor_fee', 'DF')}</th>
                <th>{print_lang('status', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedDentists.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-gray-500 py-8">
                    {print_lang('no_dentists_in_branch', 'No dentists assigned to this branch')}
                  </td>
                </tr>
              ) : (
                paginatedDentists.map((dentist) => (
                  <tr key={dentist.work_id}>
                    <td className="w-12">
                      {dentist.images_path ? (
                        <img
                          src={getImageUrl(dentist.images_path)}
                          alt={dentist.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <Stethoscope size={20} className="text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td>
                      <div>
                        <p className="font-medium">{dentist.name}</p>
                        {dentist.email && (
                          <p className="text-sm text-gray-500">{dentist.email}</p>
                        )}
                      </div>
                    </td>
                    <td>{specialtyLabels[dentist.specialty] || dentist.specialty || '-'}</td>
                    <td className="font-mono text-sm">{dentist.license_number || '-'}</td>
                    <td>
                      {dentist.df_percentage
                        ? (dentist.df_type === 'fixed'
                            ? `฿${dentist.df_percentage}`
                            : `${dentist.df_percentage}%`)
                        : '-'
                      }
                    </td>
                    <td>
                      <span className={`badge ${dentist.work_status === 'active' ? 'badge-success' : 'badge-error'}`}>
                        {print_lang(dentist.work_status, dentist.work_status === 'active' ? 'Active' : 'Inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleView(dentist)}
                          className="p-2 hover:bg-gray-100 rounded"
                          title={print_lang('view', 'View')}
                        >
                          <Eye size={16} className="text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleEdit(dentist)}
                          className="p-2 hover:bg-gray-100 rounded"
                          title={print_lang('edit', 'Edit')}
                        >
                          <Edit size={16} className="text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleRemove(dentist.work_id, dentist.name)}
                          className="p-2 hover:bg-gray-100 rounded"
                          title={print_lang('remove_from_branch', 'Remove from Branch')}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!isLoading && totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            print_lang={print_lang}
          />
        )}
      </div>

      {/* View/Edit Modal */}
      {showModal && selectedDentist && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {modalMode === 'view'
                  ? print_lang('dentist_details', 'Dentist Details')
                  : print_lang('edit_assignment', 'Edit Assignment')
                }
              </h2>
              <button onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            {modalMode === 'view' ? (
              /* View Mode */
              <div className="space-y-6">
                {/* Photo Display */}
                <div className="text-center">
                  {selectedDentist.images_path ? (
                    <img
                      src={getImageUrl(selectedDentist.images_path)}
                      alt={selectedDentist.name}
                      className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mx-auto border-4 border-gray-200">
                      <Stethoscope size={48} className="text-gray-300" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('name', 'Name')}</label>
                    <p className="font-medium">{selectedDentist.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('email', 'Email')}</label>
                    <p>{selectedDentist.email || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('phone', 'Phone')}</label>
                    <p>{selectedDentist.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('specialty', 'Specialty')}</label>
                    <p>{specialtyLabels[selectedDentist.specialty] || selectedDentist.specialty || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('license_number', 'License Number')}</label>
                    <p className="font-mono">{selectedDentist.license_number || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('joined_at', 'Joined Branch')}</label>
                    <p>{selectedDentist.joined_at ? new Date(selectedDentist.joined_at).toLocaleDateString() : '-'}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">{print_lang('branch_settings', 'Branch Settings')}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">{print_lang('df_type', 'DF Type')}</label>
                      <p>{selectedDentist.df_type === 'fixed'
                        ? print_lang('fixed', 'Fixed Amount')
                        : print_lang('percentage', 'Percentage')}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">{print_lang('doctor_fee', 'Doctor Fee')}</label>
                      <p className="font-medium">
                        {selectedDentist.df_type === 'fixed'
                          ? `฿${selectedDentist.df_percentage || 0}`
                          : `${selectedDentist.df_percentage || 0}%`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">{print_lang('status', 'Status')}</label>
                      <span className={`badge ${selectedDentist.work_status === 'active' ? 'badge-success' : 'badge-error'}`}>
                        {print_lang(selectedDentist.work_status, selectedDentist.work_status === 'active' ? 'Active' : 'Inactive')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button onClick={closeModal} className="btn btn-secondary">
                    {print_lang('close', 'Close')}
                  </button>
                  <button onClick={handleSwitchToEdit} className="btn btn-primary flex items-center gap-2">
                    <Edit size={18} />
                    {print_lang('edit_settings', 'Edit Settings')}
                  </button>
                </div>
              </div>
            ) : (
              /* Edit Mode - Only branch-specific settings */
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Dentist Info (Read-only) */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      {selectedDentist.images_path ? (
                        <img
                          src={getImageUrl(selectedDentist.images_path)}
                          alt={selectedDentist.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <Stethoscope size={24} className="text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-lg">{selectedDentist.name}</p>
                        <p className="text-gray-500">{selectedDentist.email}</p>
                        <p className="text-sm text-gray-400">{selectedDentist.license_number}</p>
                      </div>
                    </div>
                  </div>

                  {/* Editable Branch Settings */}
                  <div>
                    <h3 className="font-medium mb-4">{print_lang('branch_settings', 'Branch Settings')}</h3>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">{print_lang('df_type', 'DF Type')}</label>
                        <select
                          value={formData.df_type}
                          onChange={(e) => setFormData({ ...formData, df_type: e.target.value })}
                        >
                          <option value="">{print_lang('use_default', 'Use Default')}</option>
                          <option value="percentage">{print_lang('percentage', 'Percentage')}</option>
                          <option value="fixed">{print_lang('fixed', 'Fixed Amount')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {print_lang('doctor_fee', 'Doctor Fee')}
                          {formData.df_type === 'fixed' ? ' (฿)' : formData.df_type === 'percentage' ? ' (%)' : ''}
                        </label>
                        <input
                          type="number"
                          value={formData.df_percentage}
                          onChange={(e) => setFormData({ ...formData, df_percentage: e.target.value })}
                          min="0"
                          max={formData.df_type === 'percentage' ? '100' : undefined}
                          step="0.01"
                          placeholder={print_lang('use_default', 'Use Default')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">{print_lang('status', 'Status')}</label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                          <option value="active">{print_lang('active', 'Active')}</option>
                          <option value="inactive">{print_lang('inactive', 'Inactive')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    {print_lang('cancel', 'Cancel')}
                  </button>
                  <button type="submit" disabled={isSaving} className="btn btn-primary flex items-center gap-2">
                    <Save size={18} />
                    {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Dentist to Branch Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {print_lang('add_dentist_to_branch', 'Add Dentist to Branch')}
              </h2>
              <button onClick={() => setShowAddModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Search Box */}
              <div>
                <label className="block text-sm font-medium mb-2">{print_lang('search_dentist', 'Search Dentist')}</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={searchDentist}
                      onChange={(e) => setSearchDentist(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchDentist()}
                      placeholder={print_lang('enter_name_or_email', 'Enter name or email...')}
                      className="pl-10"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSearchDentist}
                    disabled={isSearching}
                    className="btn btn-primary"
                  >
                    {isSearching ? '...' : print_lang('search', 'Search')}
                  </button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {print_lang('search_results', 'Search Results')} ({searchResults.length})
                  </label>
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    {searchResults.map(dentist => (
                      <div
                        key={dentist.dentist_id}
                        onClick={() => handleSelectDentist(dentist)}
                        className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                          addFormData.dentist_id === dentist.dentist_id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        {dentist.images_path ? (
                          <img
                            src={getImageUrl(dentist.images_path)}
                            alt={dentist.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Stethoscope size={16} className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{dentist.name}</p>
                          <p className="text-sm text-gray-500">{dentist.email}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-gray-600">{specialtyLabels[dentist.specialty] || dentist.specialty}</p>
                          <p className="text-gray-400">{dentist.license_number}</p>
                        </div>
                        {addFormData.dentist_id === dentist.dentist_id && (
                          <div className="text-blue-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Dentist Settings */}
              {addFormData.selectedDentist && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium mb-3">{print_lang('selected_dentist', 'Selected Dentist')}: {addFormData.selectedDentist.name}</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('df_type_override', 'DF Type (Override)')}
                      </label>
                      <select
                        value={addFormData.df_type}
                        onChange={(e) => setAddFormData({ ...addFormData, df_type: e.target.value })}
                      >
                        <option value="">{print_lang('use_default', 'Use Default')}</option>
                        <option value="percentage">{print_lang('percentage', 'Percentage')}</option>
                        <option value="fixed">{print_lang('fixed', 'Fixed Amount')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('df_override', 'DF Override')}
                      </label>
                      <input
                        type="number"
                        value={addFormData.df_percentage}
                        onChange={(e) => setAddFormData({ ...addFormData, df_percentage: e.target.value })}
                        placeholder={print_lang('leave_empty_for_default', 'Leave empty for default')}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button type="button" onClick={() => setShowAddModal(false)} className="btn btn-secondary">
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddToBranch}
                disabled={!addFormData.dentist_id || isSaving}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                {isSaving ? print_lang('adding', 'Adding...') : print_lang('add_to_branch', 'Add to Branch')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DentistManagement;
